<template>
  <v-card>
    <v-card-title>Add Custom Domain</v-card-title>
    <v-card-text>
      <generic-search
        v-if="!isEditing"
        v-model="model.ownable"
        label="Advisor"
        placeholder="Search Advisors"
        :composable="useAdvisorSearch"
        data-testid="custom-domain-advisor"
        :prepend-inner-icon="mdiAccountTie"
        v-bind="ownableValidation"
      />

      <text-field
        v-model="model.domain"
        label="Domain"
        data-testid="custom-domain-domain"
        :prepend-inner-icon="mdiWeb"
        v-bind="domainValidation"
      />

      <checkbox-field
        v-model="model.default"
        data-testid="custom-domain-default"
        hide-details
        label="Default"
      />
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <app-button
        variant="outlined"
        class="text-none"
        @click="dialog.closeDialog()"
      >
        Cancel
      </app-button>
      <app-button
        color="primary"
        class="text-none"
        data-testid="custom-domain-save"
        :loading="loading"
        @click="save"
      >
        {{ isEditing ? "Save" : "Add" }}
      </app-button>
    </v-card-actions>
  </v-card>
</template>
<script setup>
import GenericSearch from "@/components/shared/GenericSearch.vue";
import { useDialogStore } from "@/stores/dialog";
import { useSnackbarStore } from "@/stores/snackbar";
import {
  createCustomDomain,
  updateCustomDomain
} from "@/api/custom-domains.service";
import { ref } from "vue";
import { computedValidation, parseErrorMessage } from "@/util/helpers";
import { CustomDomain } from "@/models/CustomDomain";
import { url } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { mdiAccountTie, mdiWeb } from "@mdi/js";
import { useAdvisorSearch } from "@/components/shared/search/advisor-search";
const props = defineProps({
  modelValue: {
    type: Object,
    default: null,
    optional: true
  }
});

const snackbar = useSnackbarStore();
const dialog = useDialogStore();

const model = ref(CustomDomain(props.modelValue));
const loading = ref(false);

const isEditing = model.value.id;

const v$ = useVuelidate(
  {
    model: {
      domain: {
        validUrl: value => {
          if (!value) return true;
          if (value.includes("http://") || value.includes("https://"))
            return false;
          return url.$validator(`https://${value}`);
        }
      },
      ownable: {
        required: v => Boolean(v?.id && v?.type)
      }
    }
  },
  { model },
  { $scope: null, $autoDirty: true }
);

const domainValidation = computedValidation(v$.value.model.domain, {
  validUrl: "Must be a valid URL that does not begin with http or https"
});

const ownableValidation = computedValidation(v$.value.model.ownable, {
  required: "Required"
});

async function save() {
  const isValid = await v$.value.$validate();
  if (!isValid) return;
  loading.value = true;

  try {
    if (isEditing) {
      await updateCustomDomain(model.value.id, {
        default: model.value.default,
        domain: model.value.domain
      });
    } else {
      await createCustomDomain({
        default: model.value.default,
        ownable_id: model.value.ownable.id,
        ownable_type: model.value.ownable.type,
        domain: model.value.domain
      });
    }
    dialog.closeDialog({ refresh: true });
  } catch (e) {
    snackbar.showErrorSnackbar({ message: parseErrorMessage(e) });
  } finally {
    loading.value = false;
  }
}
</script>
